import React, { useEffect, useRef } from 'react';
import { renderToString } from 'react-dom/server';
import { createRoot } from 'react-dom/client';
import { useMap } from 'react-leaflet';

import CustomReactPopup from './CustomReactPopup';
import PaginatedPopup from './PaginatedPopup';

const LayerComp = ({
    layer,
    selectedLayerSet,
    sourceDataRef,
    mapZoom,
    setMapLayers,
    setLayerExtent,
    setLayersLoading,
    setMapDemographicContent,
    currentTab,
}) => {
    // // Leaflet Hook // //
    const map = useMap();

    const isCancelled = useRef();
    const layerExists = useRef(false);
    // Holds the react root rendering this layer's paginated popup, if it has one, so that
    // it can be torn down when this component is destroyed:
    const popupRootRef = useRef(null);

    // // Effects // //

    // Note: linter wants unchanging useState setter functions setLayerExtent, setLayersLoading, and setMapDemographicContent (passed as props to this component) to be included in the dependency array -- will disable warning in line before deps array below
    useEffect(() => {
        // This method removes previous feature layers before rendering a new one, needed for changing layers via time slider.
        const clearFeatureLayers = () => {
            map.eachLayer((mapLayer) => {
                // eslint-disable-next-line no-underscore-dangle
                if (!mapLayer._tiles) {
                    // This prevents the base maps from being removed when clearing old maps during timeslider change event. Only basemaps contain the _tiles key.
                    map.removeLayer(mapLayer);
                }
            });
        };

        const setLayer = () => {
            // Set isCancelled to false when this component "mounts"
            isCancelled.current = false;

            layer.esriLayer.on({
                loading: () => {
                    // When first adding the layer to the map, layerExists.current should be false (initialized false in useRef hook). But should this layer attempt to load again, after it has loaded once before (as it does when panning/zooming), then this will *reset* the ref to false when the load begins but before it is complete.
                    layerExists.current = false;

                    // Add this layer to the layersLoading array in HealthTopic.js state.
                    // (NOTE: Must use callback to get the most up-to-date state variable. Accessing layersLoading from anywhere outside of that callback will return a stale value)
                    setLayersLoading((layersLoading) => [
                        ...layersLoading,
                        layer.id,
                    ]);
                },
                load: (e) => {
                    // Remove this layer from the layersLoading array in HealthTopic.js state.
                    setLayersLoading((layersLoading) =>
                        layersLoading.filter((id) => id !== layer.id)
                    );

                    // Grab an arbitrary feature to set "layerYear" field in topics.js config file for current loaded layer:
                    const sampleFeatureProperties = Object.values(
                        // eslint-disable-next-line no-underscore-dangle
                        e.target._layers
                    )[0]?.feature?.properties; // Verifying that all properties exist before continuing.

                    const [
                        currentLayerMetaData,
                    ] = selectedLayerSet.setLayers.filter(
                        (set) => set.id === e.target.options.id
                    );

                    if (currentLayerMetaData) {
                        const { yearKey } = currentLayerMetaData;

                        const layerYear = sampleFeatureProperties
                            ? sampleFeatureProperties[yearKey]
                            : null;

                        currentLayerMetaData.layerYear = layerYear;
                    }

                    // Set this ref to true so we can identify that the layer exists in the map later during cleanup:
                    layerExists.current = true;

                    if (isCancelled.current) {
                        // If this on load event handler runs after this component is destroyed, will remove layer here instead of in effect's cleanup function.
                        // (Running layer.remove() too early, e.g. after the layer begins loading but before the layer has fully loaded, will not successfully remove the layer).
                        layer.esriLayer.remove();
                    } else {
                        // Stack layers according to zIndex set in topics.js
                        const { zIndexPosition } = e.target.options;
                        if (zIndexPosition === 'back') {
                            layer.esriLayer.bringToBack();
                        }
                        if (zIndexPosition === 'front') {
                            layer.esriLayer.bringToFront();
                        }

                        // Set table data if layer is our "sourceLayer", the layer set up in the ArcGIS service that joins all relevant layers
                        // Also check if the layer fetch has been cancelled
                        if (
                            currentTab.mapSettings.timeSlider ||
                            (e.target.options.sourceLayer &&
                                !isCancelled.current)
                        ) {
                            sourceDataRef.current = e.target;
                            setLayerExtent();
                        }

                        // Bind popup if not already bound:
                        // eslint-disable-next-line no-underscore-dangle
                        if (!e.target._popup) {
                            const layerId = e.target.options.id;

                            // "popupPages" is an optional layer set configuration which
                            // splits the popup into one page per map layer. It is absent
                            // for every layer set which uses a standard single page popup.
                            const { popupPages } = selectedLayerSet;
                            const hasPopupPages =
                                Array.isArray(popupPages) &&
                                popupPages.length > 0;

                            // The first page names the layer which is clicked and carries
                            // the popup:
                            const isPaginatedAnchor =
                                hasPopupPages &&
                                popupPages[0].layerId === layerId;

                            // Layers named by any other page only supply data to that page,
                            // which is read from the clicked point, so they get no popup of
                            // their own:
                            const isPopupPageSibling =
                                hasPopupPages &&
                                popupPages
                                    .slice(1)
                                    .some((page) => page.layerId === layerId);

                            if (isPaginatedAnchor) {
                                // The paginated popup is interactive, so it is rendered
                                // into a container element with its own react root rather
                                // than bound as a static html string:
                                const container = document.createElement('div');
                                const root = createRoot(container);
                                popupRootRef.current = root;

                                let popupOpenCount = 0;

                                layer.esriLayer
                                    // Note: maxWidth must be a number -- leaflet does arithmetic
                                    // on it, so a value like 'auto' yields a width of
                                    // "NaNpx" and the popup is left unclamped:
                                    .bindPopup(container, { maxWidth: 300 })
                                    .on(
                                        'popupopen',
                                        ({ layer: { feature }, popup }) => {
                                            const clickedLatLng = popup.getLatLng();
                                            popupOpenCount += 1;

                                            root.render(
                                                <PaginatedPopup
                                                    // Keyed on each opening of the popup so
                                                    // that it remounts and returns to its
                                                    // first page with every new click:
                                                    key={popupOpenCount}
                                                    pages={popupPages}
                                                    clickedLayerId={layerId}
                                                    clickedFeature={
                                                        feature.properties
                                                    }
                                                    clickedLatLng={
                                                        clickedLatLng
                                                    }
                                                    map={map}
                                                    popup={popup}
                                                    selectedLayerSet={
                                                        selectedLayerSet
                                                    }
                                                    layer={
                                                        layer.esriLayer.options
                                                    }
                                                />
                                            );

                                            // Demographic content stays tied to the clicked
                                            // feature, it does not follow the popup page:
                                            if (
                                                !layer.esriLayer.options
                                                    .omitCommunityProfile
                                            ) {
                                                setMapDemographicContent(
                                                    feature
                                                );
                                            }
                                        }
                                    )
                                    .on('popupclose', () => {
                                        setMapDemographicContent(null);
                                    });
                            } else if (!isPopupPageSibling) {
                                layer.esriLayer
                                    .bindPopup(
                                        ({ feature }) =>
                                            renderToString(
                                                <CustomReactPopup
                                                    feature={feature.properties}
                                                    selectedLayerSet={
                                                        selectedLayerSet
                                                    }
                                                    layer={
                                                        layer.esriLayer.options
                                                    }
                                                />
                                            ),
                                        { maxWidth: 'auto' }
                                    )
                                    .on('popupopen', ({ layer: { feature } }) =>
                                        layer.esriLayer.options
                                            .omitCommunityProfile
                                            ? null
                                            : setMapDemographicContent(feature)
                                    )
                                    .on('popupclose', () => {
                                        setMapDemographicContent(null);
                                    });
                            }
                        }
                    }
                },
            });

            if (currentTab.mapSettings.timeSlider) {
                layer.esriLayer.setStyle(() => ({
                    smoothFactor: 0,
                }));
            }

            // Only add layer here if layer is not scale dependent,
            // (if it is, will be added by zoomCheck effect).
            if (!layer.esriLayer.options.minScale) {
                map.whenReady(() => {
                    map.addLayer(layer.esriLayer);
                });
            }
        };
        if (currentTab.mapSettings.timeSlider) {
            clearFeatureLayers();
        }
        setLayer();
        return function cleanup() {
            // Set isCancelled to true to signal that this component has been destroyed and we should cancel any pending on load handlers
            isCancelled.current = true;

            // Tear down the paginated popup's react root, if this layer had one. Deferred
            // because react errors when a root is unmounted while it is rendering:
            if (popupRootRef.current) {
                const popupRoot = popupRootRef.current;
                popupRootRef.current = null;
                setTimeout(() => popupRoot.unmount(), 0);
            }

            // Remove layer only if it successfully loaded before this cleanup was run.
            // (If layer does not yet exist, removal will take place within on load handler for this layer)
            if (layerExists.current) {
                layer.esriLayer.remove();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layer.esriLayer.options.id, layer, sourceDataRef, map]);

    // Effect to update this layer if zoom changes
    useEffect(() => {
        const zoomCheck = () => {
            if (layer.esriLayer.options.scaleDependent) {
                if (
                    mapZoom < layer.esriLayer.options.minScale &&
                    map.hasLayer(layer.esriLayer)
                ) {
                    setMapLayers((prevMapLayers) => {
                        prevMapLayers.find(
                            (mapLayer) => mapLayer.id === layer.id
                        ).outOfRange = true;
                        return prevMapLayers;
                    });
                    layer.esriLayer.remove();
                } else if (
                    mapZoom >= layer.esriLayer.options.minScale &&
                    !map.hasLayer(layer.esriLayer)
                ) {
                    setMapLayers((prevMapLayers) => {
                        prevMapLayers.find(
                            (mapLayer) => mapLayer.id === layer.id
                        ).outOfRange = false;
                        return prevMapLayers;
                    });
                    layer.esriLayer.addTo(map);
                }
            }
        };
        zoomCheck();
    }, [layer.esriLayer, layer.id, map, mapZoom, setMapLayers]);

    // // Render // //
    return null;
};

export default LayerComp;
